import { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Upload,
  CheckCircle2,
  AlertCircle,
  X,
  Folder,
  LoaderCircle,
} from "lucide-react";
import { Button, Badge } from "../../design-system";
import "./refine-upload-tray.css";
export type UploadRowState =
  | "waiting"
  | "uploading"
  | "creating"
  | "complete"
  | "failed"
  | "skipped"
  | "cancelled";
export type UploadTrayRow = {
  id: string;
  path: string;
  state: UploadRowState;
  filesDone: number;
  filesTotal: number;
  error?: string;
  jobId?: string;
};
export type UploadTrayBatch = {
  id: string;
  customer: string;
  label: string;
  state:
    | "waiting"
    | "running"
    | "cancelling"
    | "complete"
    | "partial"
    | "cancelled";
  rows: UploadTrayRow[];
};
export type UploadTrayView = {
  batches: UploadTrayBatch[];
  notice?: { id: string; message: string; ids: string[] };
};
export type UploadTrayActions = {
  retry?: (batchId: string) => void;
  cancel?: (batchId: string) => void;
  dismiss?: (batchId: string) => void;
  openList?: (ids: string[]) => void;
  dismissNotice?: () => void;
};
export const uploadActive = (b: UploadTrayBatch) =>
  ["waiting", "running", "cancelling"].includes(b.state);
const labels: Record<UploadRowState, string> = {
  waiting: "대기",
  uploading: "올리는 중",
  creating: "만드는 중",
  complete: "완료",
  failed: "실패",
  skipped: "건너뜀",
  cancelled: "취소",
};
export function RefineUploadTray({
  view,
  actions = {},
  initialCollapsed = false,
}: {
  view: UploadTrayView;
  actions?: UploadTrayActions;
  initialCollapsed?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [expanded, setExpanded] = useState<string[]>([]);
  if (!view.batches.length && !view.notice) return null;
  const active = view.batches.filter(uploadActive).length;
  const failures = view.batches
    .flatMap((b) => b.rows)
    .filter((r) => r.state === "failed").length;
  return (
    <aside
      className={"mut-tray" + (collapsed ? " is-collapsed" : "")}
      aria-label="정형화 업로드 트레이"
      data-upload-tray
    >
      <div className="mut-heading">
        <Upload size={18} />
        <button
          aria-expanded={!collapsed}
          onClick={() => setCollapsed(!collapsed)}
        >
          <strong>업로드</strong>
          <span>
            {active
              ? active + "묶음 진행 중"
              : failures
                ? failures + "건 실패"
                : "업로드 완료"}
          </span>
          {collapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>
      {view.notice && (
        <div className="mut-notice">
          <div role="status">
            <CheckCircle2 size={16} />
            <span>{view.notice.message}</span>
          </div>
          <div>
            {!!view.notice.ids.length && (
              <Button
                size="sm"
                variant="ghost"
                disabled={!actions.openList}
                onClick={() => actions.openList?.(view.notice!.ids)}
              >
                정형화 목록 보기
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              aria-label="완료 알림 닫기"
              onClick={actions.dismissNotice}
              disabled={!actions.dismissNotice}
            >
              <X size={14} />
            </Button>
          </div>
        </div>
      )}
      {!collapsed && (
        <div className="mut-body">
          {active > 0 && (
            <p className="mut-leave">
              <AlertCircle size={14} />
              탭을 닫으면 업로드가 멈춥니다.
            </p>
          )}
          {view.batches.map((batch) => {
            const busy = uploadActive(batch),
              done = batch.rows.filter((r) =>
                ["complete", "failed", "skipped", "cancelled"].includes(
                  r.state,
                ),
              ).length;
            const files = batch.rows.reduce((n, r) => n + r.filesTotal, 0),
              uploaded = batch.rows.reduce((n, r) => n + r.filesDone, 0);
            const failed = batch.rows.filter(
                (r) => r.state === "failed",
              ).length,
              ids = batch.rows.flatMap((r) => (r.jobId ? [r.jobId] : []));
            const open = expanded.includes(batch.id);
            return (
              <section
                key={batch.id}
                className="mut-batch"
                aria-label={batch.label}
              >
                <div className="mut-batch-head">
                  <div>
                    <strong>{batch.label}</strong>
                    <small>{batch.customer}</small>
                  </div>
                  <Badge tone={failed ? "amber" : busy ? "blue" : "green"}>
                    {batch.state === "waiting"
                      ? "순서 대기"
                      : batch.state === "cancelling"
                        ? "취소 중"
                        : batch.state === "cancelled"
                          ? "취소됨"
                          : busy
                            ? "진행 중"
                            : failed
                              ? "일부 실패"
                              : "완료"}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={batch.label + " 기록 지우기"}
                    disabled={busy || !actions.dismiss}
                    onClick={() => actions.dismiss?.(batch.id)}
                  >
                    <X size={14} />
                  </Button>
                </div>
                <div className="mut-counts">
                  <span>
                    폴더 {done}/{batch.rows.length}
                  </span>
                  <span>
                    파일 {uploaded}/{files}
                  </span>
                </div>
                <progress
                  aria-label={batch.label + " 처리 진행률"}
                  max={Math.max(1, batch.rows.length + files)}
                  value={
                    done +
                    batch.rows.reduce(
                      (n, r) =>
                        n +
                        ([
                          "complete",
                          "failed",
                          "skipped",
                          "cancelled",
                        ].includes(r.state)
                          ? r.filesTotal
                          : r.filesDone),
                      0,
                    )
                  }
                />
                <div className="mut-actions">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-expanded={open}
                    onClick={() =>
                      setExpanded(
                        open
                          ? expanded.filter((id) => id !== batch.id)
                          : [...expanded, batch.id],
                      )
                    }
                  >
                    {open ? "행 접기" : "폴더별 보기"}
                    {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </Button>
                  {busy ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={batch.state === "cancelling" || !actions.cancel}
                      onClick={() => actions.cancel?.(batch.id)}
                    >
                      남은 업로드 취소
                    </Button>
                  ) : (
                    <>
                      {failed > 0 && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!actions.retry}
                          onClick={() => actions.retry?.(batch.id)}
                        >
                          실패 {failed}건만 재시도
                        </Button>
                      )}
                      {!!ids.length && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!actions.openList}
                          onClick={() => actions.openList?.(ids)}
                        >
                          만든 작업 보기 ({ids.length})
                        </Button>
                      )}
                    </>
                  )}
                </div>
                {batch.state === "cancelling" && (
                  <p className="mut-hint">
                    진행 중 요청의 결과를 확인한 뒤 멈춥니다. 이미 만든 작업은
                    유지됩니다.
                  </p>
                )}
                {batch.state === "cancelled" && (
                  <p className="mut-hint">
                    남은 업로드를 취소했습니다. 이미 만든 작업 {ids.length}개는
                    유지됩니다.
                  </p>
                )}
                {open && (
                  <ul className="mut-rows">
                    {batch.rows.map((row) => (
                      <li key={row.id}>
                        {["uploading", "creating"].includes(row.state) ? (
                          <LoaderCircle size={16} />
                        ) : (
                          <Folder size={16} />
                        )}
                        <div>
                          <strong>{row.path}</strong>
                          <span>
                            {labels[row.state]} · 파일 {row.filesDone}/
                            {row.filesTotal}
                          </span>
                          {row.error && (
                            <p
                              className={
                                row.state === "failed"
                                  ? "mut-error"
                                  : "mut-hint"
                              }
                            >
                              {row.error}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
    </aside>
  );
}
